import { useEffect, useState } from "react";
import { LayoutDashboard, FileText, ListChecks, Grid3X3, Users, Handshake, PanelBottom, Inbox, LogOut, Save, ExternalLink, Loader2, Search, Gauge, Layers, Trash2, ArrowUp, ArrowDown, Sparkles, Info, Type, Mail, MessageSquare, Scale, Download, ChevronDown, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API } from "@/lib/api";
import { legalContent } from "@/lib/legalContent";
import { FieldRenderer } from "./editors";
import SeoPanel from "./SeoPanel";
import CmsHome from "./CmsHome";

const SECTIONS = [
  { id: "home", label: "Home", icon: Gauge },
  { id: "hero", label: "Hero", icon: LayoutDashboard },
  { id: "chi_siamo", label: "Chi è Ethic", icon: Info },
  { id: "servizi", label: "Soluzioni", icon: Grid3X3 },
  { id: "pagine", label: "Pagine Soluzioni", icon: Layers },
  { id: "target", label: "Clienti", icon: Users },
  { id: "metodo", label: "Metodo", icon: ListChecks },
  { id: "chapters", label: "Manifesto", icon: FileText },
  { id: "manifesto_visual", label: "Manifesto Visual", icon: Grid3X3 },
  { id: "partners", label: "Partner", icon: Handshake },
  { id: "contatti", label: "Contatti", icon: Mail },
  { id: "cta_popup", label: "Popup CTA", icon: MessageSquare },
  { id: "footer", label: "Footer", icon: PanelBottom },
  { id: "sezioni", label: "Titoli Sezioni", icon: Type },
  { id: "seo", label: "SEO", icon: Search },
  { id: "legali", label: "Legali", icon: Scale },
  { id: "leads", label: "Richieste", icon: Inbox },
  { id: "utenti", label: "Utenti", icon: KeyRound },
];

const SCHEMAS = {
  hero: [
    { key: "line1", label: "Titolo — riga 1" },
    { key: "line2", label: "Titolo — riga 2 (sbarrata)" },
    { key: "line3", label: "Titolo — riga 3" },
    { key: "line4", label: "Titolo — riga 4 (corsivo)" },
    { key: "subtitle", label: "Sottotitolo", type: "textarea" },
    { key: "cta_primary", label: "CTA principale" },
    { key: "cta_secondary", label: "CTA secondaria" },
    { key: "caption", label: "Riga in stampatello sotto le CTA", type: "textarea" },
  ],
  chapters: [
    { key: "title", label: "Titolo capitolo" },
    { key: "new_lead", label: "Manifesto Ethic — frase guida" },
    { key: "new_points", label: "Manifesto Ethic — punti", type: "lines" },
    { key: "old_lead", label: "Vecchio approccio — frase guida" },
    { key: "old_desc", label: "Vecchio approccio — descrizione", type: "textarea" },
    { key: "old_points", label: "Vecchio approccio — punti", type: "lines" },
    { key: "key_msg", label: "Messaggio chiave (opzionale)" },
  ],
  manifesto_visual: [
    { key: "old_items", label: "Vecchio approccio — voci («Voce → risultato»)", type: "lines" },
    { key: "old_msg", label: "Vecchio approccio — messaggio", type: "textarea" },
    { key: "new_center", label: "Manifesto Ethic — nodo centrale" },
    { key: "new_items", label: "Manifesto Ethic — aree collegate", type: "lines" },
    { key: "new_regia_label", label: "Etichetta regia" },
    { key: "new_flow", label: "Fasi della regia (in ordine)", type: "lines" },
  ],
  metodo: [
    { key: "num", label: "Numero", bilingual: false },
    { key: "tab", label: "Titolo breve (tab)" },
    { key: "title", label: "Titolo completo" },
    { key: "lead", label: "Frase guida" },
    { key: "desc", label: "Descrizione", type: "textarea" },
    { key: "points", label: "Punti", type: "lines" },
  ],
  servizi: [
    { key: "id", label: "ID", bilingual: false },
    { key: "title", label: "Titolo" },
    { key: "desc", label: "Descrizione", type: "textarea" },
    { key: "items", label: "Voci", type: "lines" },
  ],
  chi_siamo: [
    { key: "heading", label: "Titolo sezione" },
    { key: "badge", label: "Badge card" },
    { key: "p1", label: "Paragrafo 1", type: "textarea" },
    { key: "p2", label: "Paragrafo 2", type: "textarea" },
    { key: "bullets", label: "Punti card", type: "lines" },
    { key: "nonSiamo", label: "Punti 'Non siamo'", type: "lines" },
    { key: "finale", label: "Frase finale" },
  ],
  energy_manager: [
    { key: "label", label: "Etichetta" },
    { key: "heading", label: "Titolo box" },
    { key: "text", label: "Testo box", type: "textarea" },
  ],
  contatti: [
    { key: "owner_email", label: "Email destinataria notifiche form", bilingual: false },
    { key: "nome", label: "Campo: Nome" },
    { key: "azienda", label: "Campo: Azienda" },
    { key: "email", label: "Campo: Email" },
    { key: "ruolo", label: "Campo: Ruolo" },
    { key: "telefono", label: "Campo: Telefono" },
    { key: "consumo", label: "Campo: Consumo" },
    { key: "tipologia", label: "Campo: Tipologia cliente" },
    { key: "scadenza", label: "Campo: Scadenza contratto" },
    { key: "bolletta", label: "Campo: Bolletta" },
    { key: "messaggio", label: "Campo: Messaggio" },
    { key: "submit", label: "Pulsante invio" },
    { key: "sending", label: "Testo invio in corso" },
    { key: "success", label: "Messaggio di successo", type: "textarea" },
    { key: "error", label: "Messaggio di errore" },
    { key: "privacy", label: "Nota privacy", type: "textarea" },
    { key: "scegli_file", label: "Pulsante allegato" },
    { key: "consumoOptions", label: "Opzioni consumo", type: "lines" },
    { key: "tipologiaOptions", label: "Opzioni tipologia cliente", type: "lines" },
  ],
  cta_popup: [
    { key: "kicker", label: "Kicker (etichetta piccola)" },
    { key: "title", label: "Titolo popup", type: "textarea" },
    { key: "text", label: "Testo popup", type: "textarea" },
    { key: "button", label: "Testo pulsante" },
  ],
  seo: [
    { key: "title", label: "Meta title" },
    { key: "description", label: "Meta description", type: "textarea" },
    { key: "keywords", label: "Keywords (separate da virgola)" },
  ],
  footer: [
    { key: "legal", label: "Dicitura legale" },
    { key: "piva", label: "P.IVA / CF" },
    { key: "note", label: "Nota payoff", type: "textarea" },
    { key: "tagline", label: "Tagline fondo pagina" },
  ],
};

const textareaCls =
  "w-full bg-onyx/60 border border-ice/10 rounded-lg px-3.5 py-2.5 text-sm text-ice outline-none focus:border-royal transition-colors font-mono";

export default function AdminDashboard({ user, onLogout }) {
  const [doc, setDoc] = useState(null);
  const [section, setSection] = useState("home");
  const editLang = "it";
  const [saving, setSaving] = useState(false);
  const [leads, setLeads] = useState(null);
  const [uploadingImg, setUploadingImg] = useState(false);

  const uploadHeroImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      patch("hero", { ...(doc.hero || {}), image: data.url });
      toast.success("Immagine caricata — salva per pubblicarla");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingImg(false);
      e.target.value = "";
    }
  };

  useEffect(() => {
    api.get("/content").then((r) => {
      const { key, ...data } = r.data;
      setDoc(data);
    });
  }, []);

  useEffect(() => {
    if (section === "leads" && leads === null) {
      api.get("/contacts").then((r) => setLeads(r.data)).catch(() => setLeads([]));
    }
  }, [section, leads]);

  const patch = (key, value) => setDoc((d) => ({ ...d, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", doc);
      toast.success("Contenuti salvati. Il sito è aggiornato.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const applySuggestion = (key, value, extra) => {
    if (key === "hero-subtitle") {
      patch("hero", { ...(doc.hero || {}), subtitle: { ...(doc.hero?.subtitle || {}), [editLang]: value } });
    } else if (key.startsWith("servizio-") && extra) {
      const arr = [...(doc.servizi || [])];
      arr[extra.index] = { ...arr[extra.index], desc: { ...(arr[extra.index].desc || {}), [editLang]: value } };
      patch("servizi", arr);
    } else {
      patch("seo", { ...(doc.seo || {}), [key]: { ...(doc.seo?.[key] || {}), [editLang]: value } });
    }
  };

  const improveAllAndSave = async () => {
    const { data } = await api.post("/seo/analyze");
    if (!data.ai) throw new Error("AI non disponibile");
    const ai = data.ai;
    const newDoc = { ...doc };
    newDoc.seo = {
      ...(doc.seo || {}),
      title: { ...(doc.seo?.title || {}), it: ai.title_it ?? doc.seo?.title?.it },
      description: { ...(doc.seo?.description || {}), it: ai.description_it ?? doc.seo?.description?.it },
      keywords: { ...(doc.seo?.keywords || {}), it: ai.keywords_it ?? doc.seo?.keywords?.it },
    };
    if (ai.hero_subtitle_it) {
      newDoc.hero = { ...(doc.hero || {}), subtitle: { ...(doc.hero?.subtitle || {}), it: ai.hero_subtitle_it } };
    }
    if (Array.isArray(ai.servizi_desc)) {
      newDoc.servizi = (doc.servizi || []).map((s) => {
        const sug = ai.servizi_desc.find((x) => x.id === s.id);
        return sug?.desc_it ? { ...s, desc: { ...(s.desc || {}), it: sug.desc_it } } : s;
      });
    }
    setDoc(newDoc);
    await api.put("/content", newDoc);
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    onLogout();
  };

  if (!doc) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-onyx">
        <Loader2 className="animate-spin text-azure" size={28} />
      </div>
    );
  }

  const isArraySection = ["chapters", "metodo", "servizi"].includes(section);

  return (
    <div className="min-h-screen bg-onyx text-ice">
      <header className="sticky top-0 z-40 border-b border-ice/8 bg-onyx/90 backdrop-blur-md">
        <div className="px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/ethic-logo.png?v=6" alt="ETHIC ENERGY" className="h-6 w-auto" />
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground hidden sm:inline">CMS</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noreferrer" data-testid="admin-view-site" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-azure transition-colors">
              <ExternalLink size={14} /> Vedi sito
            </a>
            <button
              data-testid="admin-save-button"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 bg-royal hover:bg-azure hover:text-[#0B1424] transition-colors text-white text-sm font-semibold px-4 py-2 rounded-full disabled:opacity-60"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Salva modifiche
            </button>
            <button data-testid="admin-logout-button" onClick={logout} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-red-400 transition-colors">
              <LogOut size={14} /> Esci
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        <aside className="w-52 shrink-0 border-r border-ice/8 min-h-[calc(100vh-64px)] py-6 hidden md:block">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              data-testid={`admin-nav-${s.id}`}
              onClick={() => setSection(s.id)}
              className={`w-full flex items-center gap-3 px-6 py-3 text-sm transition-colors ${
                section === s.id ? "text-azure bg-royal/10 border-r-2 border-royal" : "text-muted-foreground hover:text-ice"
              }`}
            >
              <s.icon size={16} />
              {s.label}
            </button>
          ))}
        </aside>

        <main className="flex-1 p-6 lg:p-10 max-w-4xl">
          <div className="md:hidden flex gap-2 flex-wrap mb-6">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => setSection(s.id)}
                className={`px-3 py-1.5 rounded-full text-xs border ${section === s.id ? "border-royal bg-royal text-white" : "border-ice/10 text-muted-foreground"}`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {section === "home" ? (
            <CmsHome onImprove={improveAllAndSave} onSelect={setSection} />
          ) : section === "leads" ? (
            <LeadsTable leads={leads} onDeleted={(id) => setLeads((ls) => ls.filter((l) => l.id !== id))} />
          ) : section === "pagine" ? (
            <MacroareaEditor doc={doc} patch={patch} />
          ) : (
            <>
              <div className="flex items-center justify-between gap-4 mb-8">
                <div>
                  <h1 className="font-display text-2xl font-bold">{SECTIONS.find((s) => s.id === section)?.label}</h1>
                  <p className="mt-1 text-xs text-muted-foreground">Modifichi i testi in italiano — la versione inglese viene rigenerata automaticamente dal sito.</p>
                </div>
              </div>

              {!["leads", "home", "pagine", "sezioni", "legali", "cta_popup", "utenti"].includes(section) && (
                <div className="mb-8">
                  <SeoPanel section={section} doc={doc} editLang={editLang} onApply={applySuggestion} />
                </div>
              )}

              {section === "hero" && (
                <div className="space-y-5">
                  {SCHEMAS.hero.map((f) => (
                    <FieldRenderer key={f.key} field={f} obj={doc.hero} editLang={editLang} onChange={(v) => patch("hero", v)} />
                  ))}
                  <div>
                    <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-2">
                      Immagine hero (jpg, png, webp — max 6MB)
                    </label>
                    <div className="flex items-center gap-4 flex-wrap">
                      {doc.hero?.image && (
                        <img src={doc.hero.image} alt="Hero" className="h-24 w-auto rounded-lg border border-ice/10 object-cover" />
                      )}
                      <label
                        data-testid="admin-hero-image-upload"
                        className="inline-flex items-center gap-2 border border-dashed border-royal/50 hover:border-azure text-azure text-sm font-medium px-4 py-2.5 rounded-lg cursor-pointer transition-colors"
                      >
                        {uploadingImg ? <Loader2 size={15} className="animate-spin" /> : null}
                        {uploadingImg ? "Caricamento…" : "Carica nuova immagine"}
                        <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadHeroImage} />
                      </label>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">Dopo il caricamento premi «Salva modifiche» per pubblicare.</p>
                  </div>
                </div>
              )}

              {isArraySection && (
                <div className="space-y-6">
                  {(doc[section] || []).map((item, i) => (
                    <div key={i} className="bg-surface border border-ice/8 rounded-xl p-5 space-y-4" data-testid={`admin-${section}-item-${i}`}>
                      <p className="font-mono text-xs uppercase tracking-[0.15em] text-royal">
                        {item.num || item.id || `#${i + 1}`}
                      </p>
                      {SCHEMAS[section].map((f) => (
                        <FieldRenderer
                          key={f.key}
                          field={f}
                          obj={item}
                          editLang={editLang}
                          onChange={(v) => {
                            const arr = [...doc[section]];
                            arr[i] = v;
                            patch(section, arr);
                          }}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {section === "target" && <TargetEditor doc={doc} patch={patch} />}

              {section === "partners" && <PartnersEditor doc={doc} patch={patch} />}

              {["footer", "seo", "chi_siamo", "contatti", "cta_popup", "manifesto_visual"].includes(section) && (
                <div className="space-y-5">
                  {SCHEMAS[section].map((f) => (
                    <FieldRenderer key={f.key} field={f} obj={doc[section]} editLang={editLang} onChange={(v) => patch(section, v)} />
                  ))}
                  {section === "contatti" && <TestEmailButton ownerEmail={doc.contatti?.owner_email} />}
                </div>
              )}

              {section === "chi_siamo" && (
                <div className="mt-10 border-t border-ice/8 pt-8 space-y-5">
                  <h2 className="font-display text-lg font-semibold">Box «Al fianco dell’Energy Manager»</h2>
                  {SCHEMAS.energy_manager.map((f) => (
                    <FieldRenderer key={f.key} field={f} obj={doc.energy_manager} editLang={editLang} onChange={(v) => patch("energy_manager", v)} />
                  ))}
                </div>
              )}

              {section === "cta_popup" && (
                <div className="mb-6 flex items-center justify-between gap-4 bg-surface border border-ice/8 rounded-xl p-5">
                  <div>
                    <p className="font-display font-semibold text-ice">Popup attivo</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Se disattivato, il popup non appare più in home. I testi restano salvati.</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span
                      data-testid="cta-popup-toggle-label"
                      className={`font-mono text-xs font-semibold ${(doc.cta_popup?.attiva ?? true) ? "text-royal" : "text-muted-foreground"}`}
                    >
                      {(doc.cta_popup?.attiva ?? true) ? "ATTIVA" : "DISATTIVA"}
                    </span>
                    <button
                      data-testid="cta-popup-toggle"
                      onClick={() => patch("cta_popup", { ...(doc.cta_popup || {}), attiva: !(doc.cta_popup?.attiva ?? true) })}
                      className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors ${(doc.cta_popup?.attiva ?? true) ? "bg-royal" : "bg-ice/20"}`}
                      aria-label="Attiva o disattiva il popup"
                    >
                      <span
                        className={`inline-block h-6 w-6 rounded-full bg-white shadow transition-transform ${(doc.cta_popup?.attiva ?? true) ? "translate-x-9" : "translate-x-1"}`}
                      />
                    </button>
                  </div>
                </div>
              )}

              {section === "sezioni" && <SezioniEditor doc={doc} patch={patch} />}

              {section === "legali" && <LegalEditor doc={doc} patch={patch} />}

              {section === "utenti" && <UsersManager me={user} />}
            </>
          )}
        </main>
      </div>

      {section !== "leads" && section !== "home" && (
        <button
          data-testid="admin-save-floating"
          onClick={save}
          disabled={saving}
          className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 bg-royal hover:bg-azure hover:text-[#0B1424] transition-colors text-white text-sm font-semibold px-5 py-3 rounded-full shadow-lg shadow-royal/30 disabled:opacity-60"
        >
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
          Salva
        </button>
      )}
    </div>
  );
}

function TestEmailButton({ ownerEmail }) {
  const [sending, setSending] = useState(false);
  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/contact/test-email");
      if (data.ok) toast.success(`Email di test inviata a ${data.to}`);
      else toast.error(`Invio fallito: ${data.error || "errore sconosciuto"}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="mt-4 border-t border-ice/8 pt-5">
      <p className="text-xs text-muted-foreground mb-3">
        Invia una email SMTP di prova al destinatario attualmente <strong>salvato</strong> ({ownerEmail || "nessun destinatario"}). Se hai appena cambiato l&apos;indirizzo, premi prima «Salva».
      </p>
      <button
        data-testid="test-email-button"
        onClick={send}
        disabled={sending || !ownerEmail}
        className="inline-flex items-center gap-2 border border-royal/50 hover:border-azure text-azure text-sm font-medium px-4 py-2.5 rounded-lg transition-colors disabled:opacity-50"
      >
        {sending ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />}
        Invia email di test
      </button>
    </div>
  );
}

function PartnersEditor({ doc, patch }) {
  const partners = doc.partners || [];
  const fieldCls =
    "w-full bg-onyx/60 border border-ice/10 rounded-lg px-3.5 py-2.5 text-sm text-ice outline-none focus:border-royal transition-colors";

  const update = (i, field, value) => {
    const arr = partners.map((p, j) => {
      const obj = typeof p === "string" ? { name: p, role: "" } : p;
      return j === i ? { ...obj, [field]: value } : obj;
    });
    patch("partners", arr);
  };
  const add = () => patch("partners", [...partners, { name: "", role: "" }]);
  const remove = (i) => patch("partners", partners.filter((_, j) => j !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= partners.length) return;
    const arr = [...partners];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    patch("partners", arr);
  };

  return (
    <div data-testid="partners-editor" className="space-y-3">
      <p className="text-xs text-muted-foreground mb-2">Nome e ruolo (sottotitolo) di ogni partner. Il sito li mostra nel marquee dell’ecosistema, in quest’ordine.</p>
      {partners.map((p, i) => {
        const obj = typeof p === "string" ? { name: p, role: "" } : p;
        return (
          <div key={i} className="bg-surface border border-ice/8 rounded-xl p-4 space-y-3" data-testid={`partner-row-${i}`}>
            <div className="flex items-center justify-between">
              <span className="font-mono text-xs uppercase tracking-[0.15em] text-royal">Partner #{i + 1}</span>
              <div className="flex items-center gap-3 shrink-0">
                <button
                  data-testid={`partner-up-${i}`}
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  className="text-muted-foreground hover:text-azure disabled:opacity-25 transition-colors"
                >
                  <ArrowUp size={14} />
                </button>
                <button
                  data-testid={`partner-down-${i}`}
                  onClick={() => move(i, 1)}
                  disabled={i === partners.length - 1}
                  className="text-muted-foreground hover:text-azure disabled:opacity-25 transition-colors"
                >
                  <ArrowDown size={14} />
                </button>
                <button
                  data-testid={`partner-remove-${i}`}
                  onClick={() => remove(i)}
                  className="text-muted-foreground hover:text-red-400 transition-colors"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-1.5">Nome</label>
              <input
                data-testid={`partner-name-${i}`}
                value={obj.name}
                onChange={(e) => update(i, "name", e.target.value)}
                placeholder="Nome partner"
                className={fieldCls}
              />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-1.5">Ruolo (sottotitolo mostrato sotto il nome)</label>
              <input
                data-testid={`partner-role-${i}`}
                value={obj.role}
                onChange={(e) => update(i, "role", e.target.value)}
                placeholder="Es. Forniture energia e gas per imprese"
                className={fieldCls}
              />
            </div>
          </div>
        );
      })}
      <button
        data-testid="partner-add"
        onClick={add}
        className="mt-2 inline-flex items-center gap-2 border border-dashed border-royal/50 hover:border-azure text-azure text-sm font-medium px-4 py-2.5 rounded-lg transition-colors"
      >
        + Aggiungi partner
      </button>
    </div>
  );
}

function MacroareaEditor({ doc, patch }) {
  const [area, setArea] = useState("energy-management");
  const areas = doc.macroaree || {};
  const current = areas[area] || {};
  const charts = current.charts || [];
  const fieldCls =
    "w-full bg-onyx/60 border border-ice/10 rounded-lg px-3.5 py-2.5 text-sm text-ice outline-none focus:border-royal transition-colors";
  const labelCls = "block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-1.5";

  const patchArea = (next) => patch("macroaree", { ...areas, [area]: next });
  const setField = (field, value) =>
    patchArea({ ...current, [field]: { ...(current[field] || {}), it: value } });

  const [imgLoading, setImgLoading] = useState(false);
  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      patchArea({ ...current, image: data.url });
      toast.success("Immagine caricata — salva per pubblicarla");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setImgLoading(false);
      e.target.value = "";
    }
  };

  const slugify = (s) =>
    s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  const [aiLoading, setAiLoading] = useState(false);
  const genSeo = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post(`/seo/macroarea/${area}`);
      const next = { ...current };
      if (data.seo_title_it) next.seo_title = { ...(next.seo_title || {}), it: data.seo_title_it };
      if (data.seo_desc_it) next.seo_desc = { ...(next.seo_desc || {}), it: data.seo_desc_it };
      patchArea(next);
      toast.success("SEO generata con AI — salva per pubblicarla");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAiLoading(false);
    }
  };

  // ---- charts ----
  const setChart = (ci, next) => {
    const arr = [...charts];
    arr[ci] = next;
    patchArea({ ...current, charts: arr });
  };
  const cf = (ci, field, value) => setChart(ci, { ...charts[ci], [field]: { ...(charts[ci][field] || {}), it: value } });
  const cfPlain = (ci, field, value) => setChart(ci, { ...charts[ci], [field]: value });
  const moveChart = (ci, dir) => {
    const arr = [...charts];
    const j = ci + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[ci], arr[j]] = [arr[j], arr[ci]];
    patchArea({ ...current, charts: arr });
  };
  const addChart = () =>
    patchArea({
      ...current,
      charts: [
        ...charts,
        { id: `chart-${Date.now()}`, num: String(charts.length + 1).padStart(2, "0"), stato: "bozza", title: { it: "" }, sub: { it: "" }, services: [] },
      ],
    });
  const removeChart = (ci) => patchArea({ ...current, charts: charts.filter((_, j) => j !== ci) });

  // ---- servizi dentro una chart ----
  const setSvc = (ci, si, field, value) => {
    const svcs = [...(charts[ci].services || [])];
    svcs[si] = { ...svcs[si], [field]: { ...(svcs[si][field] || {}), it: value } };
    setChart(ci, { ...charts[ci], services: svcs });
  };
  const setSvcPlain = (ci, si, field, value) => {
    const svcs = [...(charts[ci].services || [])];
    svcs[si] = { ...svcs[si], [field]: value };
    setChart(ci, { ...charts[ci], services: svcs });
  };
  const addSvc = (ci) =>
    setChart(ci, {
      ...charts[ci],
      services: [...(charts[ci].services || []), { slug: `servizio-${Date.now()}`, title: { it: "" }, desc: { it: "" }, partner: "", cluster: "", stato: "pubblicato" }],
    });
  const removeSvc = (ci, si) =>
    setChart(ci, { ...charts[ci], services: (charts[ci].services || []).filter((_, j) => j !== si) });
  const moveSvcToChart = (ci, si, targetCi) => {
    const svc = charts[ci].services[si];
    const arr = charts.map((c, j) => {
      if (j === ci) return { ...c, services: c.services.filter((_, k) => k !== si) };
      if (j === targetCi) return { ...c, services: [...c.services, svc] };
      return c;
    });
    patchArea({ ...current, charts: arr });
  };

  // ---- cta finale / trasversale ----
  const setCta = (field, value) =>
    patchArea({ ...current, cta_finale: { ...(current.cta_finale || {}), [field]: { ...((current.cta_finale || {})[field] || {}), it: value } } });
  const setTrasv = (field, value) =>
    patchArea({ ...current, trasversale: { ...(current.trasversale || {}), [field]: { ...((current.trasversale || {})[field] || {}), it: value } } });
  const toggleTrasv = (on) =>
    patchArea({ ...current, trasversale: on ? { title: { it: "" }, text: { it: "" } } : null });

  return (
    <div data-testid="macroarea-editor">
      <h1 className="font-display text-2xl font-bold mb-2">Pagine macroarea</h1>
      <p className="text-xs text-muted-foreground mb-6">Contenuti delle pagine /soluzioni/*.</p>
      <div className="flex gap-2 flex-wrap mb-8">
        {Object.entries(areas).map(([key, a]) => (
          <button
            key={key}
            data-testid={`macroarea-tab-${key}`}
            onClick={() => setArea(key)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              area === key ? "bg-royal text-white" : "text-muted-foreground hover:text-ice border border-ice/10"
            }`}
          >
            {a.num} · {a.title?.it}
          </button>
        ))}
      </div>
      <div className="space-y-5">
        <div>
          <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-1.5">Titolo pagina (H1)</label>
          <input data-testid="macroarea-title" value={current.title?.it || ""} onChange={(e) => setField("title", e.target.value)} className={fieldCls} />
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-1.5">Payoff</label>
          <input data-testid="macroarea-payoff" value={current.payoff?.it || ""} onChange={(e) => setField("payoff", e.target.value)} className={fieldCls} />
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-1.5">Introduzione</label>
          <textarea data-testid="macroarea-intro" rows={4} value={current.intro?.it || ""} onChange={(e) => setField("intro", e.target.value)} className={fieldCls} />
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-2">Immagine hero pagina (jpg, png, webp — max 6MB)</label>
          <div className="flex items-center gap-4 flex-wrap">
            {current.image && (
              <img src={current.image} alt={current.title?.it || area} className="h-24 w-auto rounded-lg border border-ice/10 object-cover" />
            )}
            <label
              data-testid="macroarea-image-upload"
              className="inline-flex items-center gap-2 border border-dashed border-royal/50 hover:border-azure text-azure text-sm font-medium px-4 py-2.5 rounded-lg cursor-pointer transition-colors"
            >
              {imgLoading ? <Loader2 size={15} className="animate-spin" /> : null}
              {imgLoading ? "Caricamento…" : "Carica nuova immagine"}
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadImage} />
            </label>
          </div>
        </div>
        <div className="border-t border-ice/8 pt-5">
          <div className="flex items-center justify-between gap-4 mb-4">
            <p className="font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground">SEO pagina</p>
            <button
              data-testid="macroarea-seo-ai-button"
              onClick={genSeo}
              disabled={aiLoading}
              className="inline-flex items-center gap-2 bg-royal hover:bg-azure hover:text-[#0B1424] transition-colors text-white text-xs font-semibold px-4 py-2 rounded-full disabled:opacity-60"
            >
              {aiLoading ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
              {aiLoading ? "Generazione…" : "Genera SEO con AI"}
            </button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-1.5">SEO title (titolo tab)</label>
              <input data-testid="macroarea-seo-title" value={current.seo_title?.it || ""} onChange={(e) => setField("seo_title", e.target.value)} className={fieldCls} />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-1.5">SEO description</label>
              <textarea data-testid="macroarea-seo-desc" rows={2} value={current.seo_desc?.it || ""} onChange={(e) => setField("seo_desc", e.target.value)} className={fieldCls} />
            </div>
          </div>
        </div>
        <div className="border-t border-ice/8 pt-5">
          <label className={labelCls}>Sotto-aree della pagina ({charts.length})</label>
          <p className="text-xs text-muted-foreground mb-4">
            Le sotto-aree sono il livello visivo principale della pagina. Stato «bozza» = nascosta dal sito. I servizi si possono spostare tra sotto-aree senza toccare il codice.
          </p>
          <div className="space-y-4">
            {charts.map((ch, ci) => (
              <details key={ch.id || ci} className="bg-surface border border-ice/8 rounded-xl" data-testid={`macroarea-chart-${ci}`}>
                <summary className="cursor-pointer px-5 py-4 text-sm font-semibold text-ice flex items-center gap-3 select-none">
                  <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-royal bg-royal/10 rounded-full px-2.5 py-1">
                    {ch.num || `#${ci + 1}`}
                  </span>
                  <span className="truncate">{(ch.title?.it || "Senza titolo").slice(0, 60)}</span>
                  {ch.stato === "bozza" && (
                    <span className="font-mono text-[10px] uppercase tracking-wide text-amber-500 border border-amber-500/40 rounded-full px-2 py-0.5 shrink-0">bozza</span>
                  )}
                </summary>
                <div className="px-5 pb-5 pt-4 border-t border-ice/8 space-y-4">
                  <div className="flex items-end gap-3 flex-wrap">
                    <div className="w-24">
                      <label className={labelCls}>Numero</label>
                      <input data-testid={`macroarea-chart-${ci}-num`} value={ch.num || ""} onChange={(e) => cfPlain(ci, "num", e.target.value)} className={fieldCls} />
                    </div>
                    <div className="w-48">
                      <label className={labelCls}>Stato</label>
                      <select data-testid={`macroarea-chart-${ci}-stato`} value={ch.stato || "pubblicato"} onChange={(e) => cfPlain(ci, "stato", e.target.value)} className={fieldCls}>
                        <option value="pubblicato">Pubblicata</option>
                        <option value="bozza">Bozza (nascosta)</option>
                      </select>
                    </div>
                    <div className="flex gap-1.5 ml-auto pb-0.5">
                      <button data-testid={`macroarea-chart-${ci}-up`} onClick={() => moveChart(ci, -1)} disabled={ci === 0} className="p-2 rounded-lg border border-ice/10 text-muted-foreground hover:text-ice hover:border-royal/50 disabled:opacity-30 transition-colors">
                        <ArrowUp size={14} />
                      </button>
                      <button data-testid={`macroarea-chart-${ci}-down`} onClick={() => moveChart(ci, 1)} disabled={ci === charts.length - 1} className="p-2 rounded-lg border border-ice/10 text-muted-foreground hover:text-ice hover:border-royal/50 disabled:opacity-30 transition-colors">
                        <ArrowDown size={14} />
                      </button>
                      <button data-testid={`macroarea-chart-${ci}-remove`} onClick={() => removeChart(ci)} className="p-2 rounded-lg text-muted-foreground hover:text-red-400 transition-colors">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Titolo sotto-area</label>
                    <input data-testid={`macroarea-chart-${ci}-title`} value={ch.title?.it || ""} onChange={(e) => cf(ci, "title", e.target.value)} className={fieldCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Sottotitolo</label>
                    <textarea data-testid={`macroarea-chart-${ci}-sub`} rows={2} value={ch.sub?.it || ""} onChange={(e) => cf(ci, "sub", e.target.value)} className={fieldCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Nota di posizionamento (opzionale, in testa alla sotto-area)</label>
                    <textarea data-testid={`macroarea-chart-${ci}-note`} rows={2} value={ch.note?.it || ""} onChange={(e) => cf(ci, "note", e.target.value)} className={fieldCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Nota a piè di sotto-area (opzionale, es. nota regolamentare)</label>
                    <input data-testid={`macroarea-chart-${ci}-note-footer`} value={ch.note_footer?.it || ""} onChange={(e) => cf(ci, "note_footer", e.target.value)} className={fieldCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Etichetta sotto le card (opzionale, es. «Tramite broker assicurativi abilitati»)</label>
                    <input data-testid={`macroarea-chart-${ci}-label`} value={ch.label?.it || ""} onChange={(e) => cf(ci, "label", e.target.value)} className={fieldCls} />
                  </div>

                  <div className="space-y-3">
                    <label className={labelCls}>Servizi ({(ch.services || []).length})</label>
                    {(ch.services || []).map((s, si) => (
                      <div key={s.slug || si} className="bg-onyx/40 rounded-lg p-3 space-y-3" data-testid={`macroarea-chart-${ci}-service-${si}`}>
                        <div className="flex items-center gap-2.5">
                          <input
                            data-testid={`macroarea-chart-${ci}-service-${si}-title`}
                            value={s.title?.it || ""}
                            onChange={(e) => setSvc(ci, si, "title", e.target.value)}
                            placeholder="Nome servizio"
                            className={fieldCls}
                          />
                          <select
                            data-testid={`macroarea-chart-${ci}-service-${si}-stato`}
                            value={s.stato || "pubblicato"}
                            onChange={(e) => setSvcPlain(ci, si, "stato", e.target.value)}
                            className={`${fieldCls} w-36 shrink-0`}
                          >
                            <option value="pubblicato">Pubblicato</option>
                            <option value="bozza">Bozza</option>
                          </select>
                          <button
                            data-testid={`macroarea-chart-${ci}-service-${si}-remove`}
                            onClick={() => removeSvc(ci, si)}
                            className="text-muted-foreground hover:text-red-400 transition-colors shrink-0"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                        <textarea
                          data-testid={`macroarea-chart-${ci}-service-${si}-desc`}
                          rows={3}
                          value={s.desc?.it || ""}
                          onChange={(e) => setSvc(ci, si, "desc", e.target.value)}
                          placeholder="Descrizione"
                          className={fieldCls}
                        />
                        <div className="grid sm:grid-cols-3 gap-3">
                          <input
                            data-testid={`macroarea-chart-${ci}-service-${si}-slug`}
                            value={s.slug || ""}
                            onChange={(e) => setSvcPlain(ci, si, "slug", e.target.value)}
                            placeholder="slug (landing futura)"
                            className={fieldCls}
                          />
                          <input
                            data-testid={`macroarea-chart-${ci}-service-${si}-partner`}
                            value={s.partner || ""}
                            onChange={(e) => setSvcPlain(ci, si, "partner", e.target.value)}
                            placeholder="Partner/presidio (es. MAG)"
                            className={fieldCls}
                          />
                          <input
                            data-testid={`macroarea-chart-${ci}-service-${si}-cluster`}
                            value={s.cluster || ""}
                            onChange={(e) => setSvcPlain(ci, si, "cluster", e.target.value)}
                            placeholder="Cluster cliente"
                            className={fieldCls}
                          />
                        </div>
                        {charts.length > 1 && (
                          <select
                            data-testid={`macroarea-chart-${ci}-service-${si}-move`}
                            value=""
                            onChange={(e) => {
                              const target = Number(e.target.value);
                              if (!Number.isNaN(target)) moveSvcToChart(ci, si, target);
                            }}
                            className={`${fieldCls} sm:w-64`}
                          >
                            <option value="">Sposta in altra sotto-area…</option>
                            {charts.map((c2, cj) =>
                              cj !== ci ? (
                                <option key={c2.id || cj} value={cj}>
                                  {c2.num} — {c2.title?.it || "Senza titolo"}
                                </option>
                              ) : null
                            )}
                          </select>
                        )}
                      </div>
                    ))}
                    <button
                      data-testid={`macroarea-chart-${ci}-service-add`}
                      onClick={() => addSvc(ci)}
                      className="inline-flex items-center gap-2 border border-dashed border-royal/50 hover:border-azure text-azure text-sm font-medium px-4 py-2.5 rounded-lg transition-colors"
                    >
                      + Aggiungi servizio
                    </button>
                  </div>
                </div>
              </details>
            ))}
          </div>
          <button
            data-testid="macroarea-chart-add"
            onClick={addChart}
            className="mt-4 inline-flex items-center gap-2 border border-dashed border-royal/50 hover:border-azure text-azure text-sm font-medium px-4 py-2.5 rounded-lg transition-colors"
          >
            + Aggiungi sotto-area
          </button>
        </div>

        <div className="border-t border-ice/8 pt-5">
          <label className="flex items-center gap-2.5 text-sm text-ice cursor-pointer mb-4">
            <input
              type="checkbox"
              data-testid="macroarea-trasversale-toggle"
              checked={!!current.trasversale}
              onChange={(e) => toggleTrasv(e.target.checked)}
              className="accent-royal h-4 w-4"
            />
            Blocco trasversale dopo le sotto-aree
          </label>
          {current.trasversale && (
            <div className="space-y-4">
              <div>
                <label className={labelCls}>Titolo blocco trasversale</label>
                <input data-testid="macroarea-trasversale-title" value={current.trasversale?.title?.it || ""} onChange={(e) => setTrasv("title", e.target.value)} className={fieldCls} />
              </div>
              <div>
                <label className={labelCls}>Testo blocco trasversale</label>
                <textarea data-testid="macroarea-trasversale-text" rows={3} value={current.trasversale?.text?.it || ""} onChange={(e) => setTrasv("text", e.target.value)} className={fieldCls} />
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-ice/8 pt-5 space-y-4">
          <p className={labelCls}>CTA finale della pagina</p>
          <div>
            <label className={labelCls}>Headline</label>
            <input data-testid="macroarea-cta-heading" value={current.cta_finale?.heading?.it || ""} onChange={(e) => setCta("heading", e.target.value)} className={fieldCls} />
          </div>
          <div>
            <label className={labelCls}>Testo (opzionale)</label>
            <textarea data-testid="macroarea-cta-text" rows={2} value={current.cta_finale?.text?.it || ""} onChange={(e) => setCta("text", e.target.value)} className={fieldCls} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>CTA primaria</label>
              <input data-testid="macroarea-cta-primary" value={current.cta_finale?.primary?.it || ""} onChange={(e) => setCta("primary", e.target.value)} className={fieldCls} />
            </div>
            <div>
              <label className={labelCls}>CTA secondaria (opzionale)</label>
              <input data-testid="macroarea-cta-secondary" value={current.cta_finale?.secondary?.it || ""} onChange={(e) => setCta("secondary", e.target.value)} className={fieldCls} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PaginaBlocksEditor({ current, update }) {
  const fieldCls =
    "w-full bg-onyx/60 border border-ice/10 rounded-lg px-3.5 py-2.5 text-sm text-ice outline-none focus:border-royal transition-colors";
  const labelCls = "block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-1.5";
  const pagina = current.pagina || {};
  const blocks = pagina.blocks || [];

  const setPageField = (field, value) =>
    update({ ...current, pagina: { ...pagina, [field]: { ...(pagina[field] || {}), it: value } } });
  const setBlock = (bi, next) => {
    const arr = [...blocks];
    arr[bi] = next;
    update({ ...current, pagina: { ...pagina, blocks: arr } });
  };
  const bf = (bi, field, value) => setBlock(bi, { ...blocks[bi], [field]: { ...(blocks[bi][field] || {}), it: value } });
  const bfLines = (bi, field, value) =>
    setBlock(bi, {
      ...blocks[bi],
      [field]: {
        it: value
          .split("\n")
          .filter((s) => s.trim() !== "")
          .map((l) => {
            const parts = l.split(" :: ");
            return parts.length > 1 ? { t: parts[0].trim(), d: parts.slice(1).join(" :: ").trim() } : l.trim();
          }),
      },
    });
  const linesVal = (field) =>
    (field?.it || [])
      .map((p) => (typeof p === "string" ? p : p.d ? `${p.t} :: ${p.d}` : p.t))
      .join("\n");
  const setItem = (bi, listKey, ii, field, value) => {
    const list = [...(blocks[bi][listKey] || [])];
    list[ii] = { ...list[ii], [field]: { ...(list[ii][field] || {}), it: value } };
    setBlock(bi, { ...blocks[bi], [listKey]: list });
  };
  const setItemPlain = (bi, listKey, ii, field, value) => {
    const list = [...(blocks[bi][listKey] || [])];
    list[ii] = { ...list[ii], [field]: value };
    setBlock(bi, { ...blocks[bi], [listKey]: list });
  };

  const ICON_OPTIONS = [
    ["zap", "Energia (fulmine)"],
    ["settings", "Impianti (ingranaggio)"],
    ["leaf", "Ambiente (foglia)"],
    ["fuel", "Fuel/Gasolio (pompa)"],
    ["hotel", "Hospitality (hotel)"],
    ["building", "Edificio"],
    ["store", "Retail (negozio)"],
  ];

  const TIPO_LABEL = {
    cards: "Blocco card",
    blocco: "Sezione numerata",
    blocco_sub: "Sezione con sotto-box",
    personas: "Blocco ruoli/interlocutori",
    steps: "Percorso a step",
    special: "Box speciale (grafica distinta)",
    cta_finale: "CTA finale",
  };

  return (
    <div className="space-y-5" data-testid="pagina-blocks-editor">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>CTA primaria</label>
          <input data-testid="pagina-cta-primary" value={pagina.cta_primary?.it || ""} onChange={(e) => setPageField("cta_primary", e.target.value)} className={fieldCls} />
        </div>
        <div>
          <label className={labelCls}>CTA secondaria</label>
          <input data-testid="pagina-cta-secondary" value={pagina.cta_secondary?.it || ""} onChange={(e) => setPageField("cta_secondary", e.target.value)} className={fieldCls} />
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        La pagina è composta da {blocks.length} blocchi in ordine. Apri ogni blocco per modificarne i testi. Per i punti con sotto-descrizione usa il formato «Titolo :: descrizione».
      </p>

      {blocks.map((b, bi) => (
        <details key={bi} className="bg-surface border border-ice/8 rounded-xl group" data-testid={`pagina-block-${bi}`}>
          <summary className="cursor-pointer px-5 py-4 text-sm font-semibold text-ice flex items-center gap-3 select-none">
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-royal bg-royal/10 rounded-full px-2.5 py-1">
              {b.num || TIPO_LABEL[b.tipo] || b.tipo}
            </span>
            {(b.title?.it || b.heading?.it || "").slice(0, 60)}
          </summary>
          <div className="px-5 pb-5 pt-2 border-t border-ice/8 space-y-4">
            {(b.num !== undefined || b.tipo === "blocco" || b.tipo === "blocco_sub") && (
              <div className="flex items-end gap-5 flex-wrap">
                {b.num !== undefined && (
                  <div className="w-28">
                    <label className={labelCls}>Numero</label>
                    <input
                      data-testid={`pagina-block-${bi}-num`}
                      value={b.num || ""}
                      onChange={(e) => setBlock(bi, { ...blocks[bi], num: e.target.value })}
                      className={fieldCls}
                    />
                  </div>
                )}
                <label className="flex items-center gap-2.5 text-sm text-ice cursor-pointer pb-2">
                  <input
                    type="checkbox"
                    data-testid={`pagina-block-${bi}-highlight`}
                    checked={!!b.highlight}
                    onChange={(e) => setBlock(bi, { ...blocks[bi], highlight: e.target.checked })}
                    className="accent-royal h-4 w-4"
                  />
                  Evidenzia graficamente
                </label>
              </div>
            )}
            {(b.title !== undefined || ["cards", "personas", "steps", "special", "cta_finale"].includes(b.tipo)) && (
              <div>
                <label className={labelCls}>{b.tipo === "cta_finale" || b.tipo === "special" ? "Heading" : "Titolo"}</label>
                <input
                  data-testid={`pagina-block-${bi}-title`}
                  value={(b.title?.it ?? b.heading?.it) || ""}
                  onChange={(e) => bf(bi, b.title !== undefined ? "title" : "heading", e.target.value)}
                  className={fieldCls}
                />
              </div>
            )}
            {b.badge !== undefined && (
              <div>
                <label className={labelCls}>Badge</label>
                <input data-testid={`pagina-block-${bi}-badge`} value={b.badge?.it || ""} onChange={(e) => bf(bi, "badge", e.target.value)} className={fieldCls} />
              </div>
            )}
            {(b.sub !== undefined || ["blocco", "blocco_sub", "cards", "personas", "steps", "special"].includes(b.tipo)) && b.tipo !== "cta_finale" && (
              <div>
                <label className={labelCls}>Sottotitolo (corsivo)</label>
                <input data-testid={`pagina-block-${bi}-sub`} value={b.sub?.it || ""} onChange={(e) => bf(bi, "sub", e.target.value)} className={fieldCls} />
              </div>
            )}
            {(b.desc !== undefined || b.text !== undefined || ["cards", "personas", "cta_finale", "special"].includes(b.tipo)) && (
              <div>
                <label className={labelCls}>{b.tipo === "special" || b.tipo === "cta_finale" ? "Testo" : "Descrizione"}</label>
                <textarea
                  data-testid={`pagina-block-${bi}-desc`}
                  rows={3}
                  value={(b.desc?.it ?? b.text?.it) || ""}
                  onChange={(e) => bf(bi, b.desc !== undefined ? "desc" : "text", e.target.value)}
                  className={fieldCls}
                />
              </div>
            )}
            {b.points !== undefined && (
              <div>
                <label className={labelCls}>
                  Punti <span className="normal-case tracking-normal">(uno per riga; «Titolo :: descrizione» per punti estesi)</span>
                </label>
                <textarea data-testid={`pagina-block-${bi}-points`} rows={Math.max(4, (b.points?.it || []).length)} value={linesVal(b.points)} onChange={(e) => bfLines(bi, "points", e.target.value)} className={fieldCls} />
              </div>
            )}
            {b.note !== undefined && (
              <div>
                <label className={labelCls}>Messaggio chiave (callout)</label>
                <textarea data-testid={`pagina-block-${bi}-note`} rows={2} value={b.note?.it || ""} onChange={(e) => bf(bi, "note", e.target.value)} className={fieldCls} />
              </div>
            )}
            {(b.cards || []).length > 0 && (
              <div className="space-y-3">
                <label className={labelCls}>Card</label>
                {b.cards.map((c, ci) => (
                  <div key={ci} className="grid sm:grid-cols-[150px_1fr_2fr] gap-3 bg-onyx/40 rounded-lg p-3">
                    <select
                      data-testid={`pagina-block-${bi}-card-${ci}-icon`}
                      value={c.icon || "zap"}
                      onChange={(e) => setItemPlain(bi, "cards", ci, "icon", e.target.value)}
                      className={fieldCls}
                    >
                      {ICON_OPTIONS.map(([v, l]) => (
                        <option key={v} value={v}>{l}</option>
                      ))}
                    </select>
                    <input data-testid={`pagina-block-${bi}-card-${ci}-title`} value={c.title?.it || ""} onChange={(e) => setItem(bi, "cards", ci, "title", e.target.value)} className={fieldCls} placeholder="Titolo card" />
                    <textarea data-testid={`pagina-block-${bi}-card-${ci}-text`} rows={2} value={c.text?.it || ""} onChange={(e) => setItem(bi, "cards", ci, "text", e.target.value)} className={fieldCls} placeholder="Testo card" />
                  </div>
                ))}
              </div>
            )}
            {(b.personas || []).length > 0 && (
              <div className="space-y-3">
                <label className={labelCls}>Interlocutori</label>
                {b.personas.map((p, pi) => (
                  <div key={pi} className="grid sm:grid-cols-[1fr_2fr] gap-3 bg-onyx/40 rounded-lg p-3">
                    <input data-testid={`pagina-block-${bi}-persona-${pi}-title`} value={p.title?.it || ""} onChange={(e) => setItem(bi, "personas", pi, "title", e.target.value)} className={fieldCls} placeholder="Ruolo" />
                    <textarea data-testid={`pagina-block-${bi}-persona-${pi}-text`} rows={2} value={p.text?.it || ""} onChange={(e) => setItem(bi, "personas", pi, "text", e.target.value)} className={fieldCls} placeholder="Descrizione" />
                  </div>
                ))}
              </div>
            )}
            {(b.steps || []).length > 0 && (
              <div className="space-y-3">
                <label className={labelCls}>Step</label>
                {b.steps.map((s, si) => (
                  <div key={si} className="grid sm:grid-cols-[1fr_2fr] gap-3 bg-onyx/40 rounded-lg p-3">
                    <input data-testid={`pagina-block-${bi}-step-${si}-title`} value={s.title?.it || ""} onChange={(e) => setItem(bi, "steps", si, "title", e.target.value)} className={fieldCls} placeholder="Titolo step" />
                    <textarea data-testid={`pagina-block-${bi}-step-${si}-text`} rows={2} value={s.text?.it || ""} onChange={(e) => setItem(bi, "steps", si, "text", e.target.value)} className={fieldCls} placeholder="Testo step" />
                  </div>
                ))}
              </div>
            )}
            {(b.subblocks || []).length > 0 && (
              <div className="space-y-3">
                <label className={labelCls}>Sotto-box</label>
                {b.subblocks.map((sb, si) => (
                  <div key={si} className="bg-onyx/40 rounded-lg p-3 space-y-3">
                    <input data-testid={`pagina-block-${bi}-subblock-${si}-title`} value={sb.title?.it || ""} onChange={(e) => setItem(bi, "subblocks", si, "title", e.target.value)} className={fieldCls} placeholder="Titolo sotto-box" />
                    <textarea
                      data-testid={`pagina-block-${bi}-subblock-${si}-points`}
                      rows={Math.max(3, (sb.points?.it || []).length)}
                      value={(sb.points?.it || []).join("\n")}
                      onChange={(e) =>
                        setItem(bi, "subblocks", si, "points", { it: e.target.value.split("\n").filter((x) => x.trim() !== "") })
                      }
                      className={fieldCls}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </details>
      ))}
    </div>
  );
}

function TargetEditor({ doc, patch }) {
  const targets = doc.target || [];
  const [sel, setSel] = useState(targets[0]?.id || "sgr");
  const [aiLoading, setAiLoading] = useState(false);
  const [imgLoading, setImgLoading] = useState(false);
  const fieldCls =
    "w-full bg-onyx/60 border border-ice/10 rounded-lg px-3.5 py-2.5 text-sm text-ice outline-none focus:border-royal transition-colors";

  const i = targets.findIndex((t) => t.id === sel);
  const current = targets[i] || {};

  const update = (next) => {
    const arr = [...targets];
    arr[i] = next;
    patch("target", arr);
  };
  const setField = (field, value) => update({ ...current, [field]: { ...(current[field] || {}), it: value } });
  const setBenefits = (value) =>
    update({ ...current, benefits: { ...(current.benefits || {}), it: value.split("\n").filter((s) => s.trim() !== "") } });

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update({ ...current, image: data.url });
      toast.success("Immagine caricata — salva per pubblicarla");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setImgLoading(false);
      e.target.value = "";
    }
  };

  const genSeo = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post(`/seo/target-page/${sel}`);
      const next = { ...current };
      if (data.seo_title_it) next.seo_title = { ...(next.seo_title || {}), it: data.seo_title_it };
      if (data.seo_desc_it) next.seo_desc = { ...(next.seo_desc || {}), it: data.seo_desc_it };
      update(next);
      toast.success("SEO generata con AI — salva per pubblicarla");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAiLoading(false);
    }
  };

  if (!targets.length) return null;

  return (
    <div data-testid="target-editor">
      <div className="flex gap-2 flex-wrap mb-8">
        {targets.map((t) => (
          <button
            key={t.id}
            data-testid={`target-editor-tab-${t.id}`}
            onClick={() => setSel(t.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              sel === t.id ? "bg-royal text-white" : "text-muted-foreground hover:text-ice border border-ice/10"
            }`}
          >
            {t.label?.it || t.id}
          </button>
        ))}
      </div>

      <p className="text-xs text-muted-foreground mb-6">
        Contenuti della pagina <span className="font-mono text-azure">/clienti/{sel}</span> — raggiungibile cliccando la card nella sezione «A chi ci rivolgiamo».
      </p>

      <div className="space-y-5">
        <div>
          <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-1.5">Etichetta</label>
          <input data-testid="target-field-label" value={current.label?.it || ""} onChange={(e) => setField("label", e.target.value)} className={fieldCls} />
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-1.5">Headline (titolo pagina)</label>
          <textarea data-testid="target-field-headline" rows={2} value={current.headline?.it || ""} onChange={(e) => setField("headline", e.target.value)} className={fieldCls} />
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-1.5">Payoff</label>
          <input data-testid="target-field-payoff" value={current.payoff?.it || ""} onChange={(e) => setField("payoff", e.target.value)} className={fieldCls} />
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-1.5">Introduzione</label>
          <textarea data-testid="target-field-intro" rows={5} value={current.intro?.it || ""} onChange={(e) => setField("intro", e.target.value)} className={fieldCls} />
        </div>
        {current.pagina ? (
          <PaginaBlocksEditor current={current} update={update} />
        ) : (
          <div>
            <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-1.5">
              Benefici <span className="normal-case tracking-normal">(una voce per riga)</span>
            </label>
            <textarea
              data-testid="target-field-benefits"
              rows={Math.max(4, (current.benefits?.it || []).length)}
              value={(current.benefits?.it || []).join("\n")}
              onChange={(e) => setBenefits(e.target.value)}
              className={fieldCls}
            />
          </div>
        )}

        <div>
          <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-2">
            Immagine hero pagina (jpg, png, webp — max 6MB)
          </label>
          <div className="flex items-center gap-4 flex-wrap">
            {current.image && (
              <img src={current.image} alt={current.label?.it || sel} className="h-24 w-auto rounded-lg border border-ice/10 object-cover" />
            )}
            <label
              data-testid="target-image-upload"
              className="inline-flex items-center gap-2 border border-dashed border-royal/50 hover:border-azure text-azure text-sm font-medium px-4 py-2.5 rounded-lg cursor-pointer transition-colors"
            >
              {imgLoading ? <Loader2 size={15} className="animate-spin" /> : null}
              {imgLoading ? "Caricamento…" : "Carica nuova immagine"}
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadImage} />
            </label>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">Dopo il caricamento premi «Salva modifiche» per pubblicare.</p>
        </div>

        <div className="border-t border-ice/8 pt-5">
          <div className="flex items-center justify-between gap-4 mb-4">
            <p className="font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground">SEO pagina</p>
            <button
              data-testid="target-seo-ai-button"
              onClick={genSeo}
              disabled={aiLoading}
              className="inline-flex items-center gap-2 bg-royal hover:bg-azure hover:text-[#0B1424] transition-colors text-white text-xs font-semibold px-4 py-2 rounded-full disabled:opacity-60"
            >
              {aiLoading ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
              {aiLoading ? "Generazione…" : "Genera SEO con AI"}
            </button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-1.5">SEO title (titolo tab)</label>
              <input data-testid="target-seo-title" value={current.seo_title?.it || ""} onChange={(e) => setField("seo_title", e.target.value)} className={fieldCls} />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-1.5">SEO description</label>
              <textarea data-testid="target-seo-desc" rows={2} value={current.seo_desc?.it || ""} onChange={(e) => setField("seo_desc", e.target.value)} className={fieldCls} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SezioniEditor({ doc, patch }) {
  const sezioni = doc.sezioni || {};
  const fieldCls =
    "w-full bg-onyx/60 border border-ice/10 rounded-lg px-3.5 py-2.5 text-sm text-ice outline-none focus:border-royal transition-colors";
  const CFG = [
    ["manifesto", "Manifesto", [["heading", "Titolo"], ["sub", "Sottotitolo"], ["intro", "Testo introduttivo", "textarea"], ["old", "Etichetta toggle 'vecchio approccio'"], ["new", "Etichetta toggle 'nuovo approccio'"]]],
    ["metodo", "Metodo", [["heading", "Titolo"], ["claim", "Claim"], ["sub", "Testo introduttivo", "textarea"]]],
    ["servizi", "Soluzioni", [["heading", "Titolo"], ["sub", "Sottotitolo"]]],
    ["target", "Clienti", [["heading", "Titolo"]]],
    ["osservatorio", "Osservatorio Energia", [["heading", "Titolo"], ["sub", "Sottotitolo"]]],
    ["partners", "Partner", [["heading", "Titolo"], ["sub", "Sottotitolo"]]],
    ["dati", "Dati & Tracciabilità", [["heading", "Titolo"], ["sub", "Sottotitolo"]]],
    ["contatti", "Contatti", [["heading", "Titolo"], ["sub", "Sottotitolo"], ["quote", "Citazione serif accanto al form"]]],
  ];
  const setField = (sec, field, value) =>
    patch("sezioni", { ...sezioni, [sec]: { ...(sezioni[sec] || {}), [field]: { ...((sezioni[sec] || {})[field] || {}), it: value } } });

  return (
    <div data-testid="sezioni-editor" className="space-y-6">
      <p className="text-xs text-muted-foreground">Titoli e sottotitoli delle sezioni della home.</p>
      {CFG.map(([sec, label, fields]) => (
        <div key={sec} className="bg-surface border border-ice/8 rounded-xl p-5 space-y-4" data-testid={`sezioni-${sec}`}>
          <p className="font-mono text-xs uppercase tracking-[0.15em] text-royal">{label}</p>
          {fields.map(([field, flabel, ftype]) => (
            <div key={field}>
              <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-1.5">{flabel}</label>
              {ftype === "textarea" || field === "sub" || field === "quote" ? (
                <textarea
                  rows={2}
                  data-testid={`sezioni-${sec}-${field}`}
                  value={sezioni[sec]?.[field]?.it || ""}
                  onChange={(e) => setField(sec, field, e.target.value)}
                  className={fieldCls}
                />
              ) : (
                <input
                  data-testid={`sezioni-${sec}-${field}`}
                  value={sezioni[sec]?.[field]?.it || ""}
                  onChange={(e) => setField(sec, field, e.target.value)}
                  className={fieldCls}
                />
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function LegalEditor({ doc, patch }) {
  const [sel, setSel] = useState("privacy");
  const legal = doc.legal || legalContent;
  const current = legal[sel] || {};
  const fieldCls =
    "w-full bg-onyx/60 border border-ice/10 rounded-lg px-3.5 py-2.5 text-sm text-ice outline-none focus:border-royal transition-colors";

  const update = (next) => patch("legal", { ...legal, [sel]: next });
  const setMeta = (field, value) => update({ ...current, [field]: { ...(current[field] || {}), it: value } });
  const setSection = (i, next) => {
    const sections = (current.sections || []).map((s, j) => (j === i ? next : s));
    update({ ...current, sections });
  };

  return (
    <div data-testid="legal-editor">
      <div className="flex gap-2 mb-8">
        {[
          ["privacy", "Privacy Policy"],
          ["cookie", "Cookie Policy"],
        ].map(([k, label]) => (
          <button
            key={k}
            data-testid={`legal-tab-${k}`}
            onClick={() => setSel(k)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              sel === k ? "bg-royal text-white" : "text-muted-foreground hover:text-ice border border-ice/10"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mb-6">Contenuti della pagina /{sel === "privacy" ? "privacy-policy" : "cookie-policy"}.</p>

      <div className="space-y-5">
        <div>
          <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-1.5">Titolo pagina</label>
          <input data-testid="legal-title" value={current.title?.it || ""} onChange={(e) => setMeta("title", e.target.value)} className={fieldCls} />
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-1.5">Nota aggiornamento</label>
          <input data-testid="legal-updated" value={current.updated?.it || ""} onChange={(e) => setMeta("updated", e.target.value)} className={fieldCls} />
        </div>
        {(current.sections || []).map((s, i) => (
          <div key={i} className="bg-surface border border-ice/8 rounded-xl p-5 space-y-4" data-testid={`legal-section-${i}`}>
            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-1.5">Titolo sezione</label>
              <input
                data-testid={`legal-section-${i}-heading`}
                value={s.heading?.it || ""}
                onChange={(e) => setSection(i, { ...s, heading: { ...(s.heading || {}), it: e.target.value } })}
                className={fieldCls}
              />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-1.5">
                Paragrafi <span className="normal-case tracking-normal">(separati da una riga vuota)</span>
              </label>
              <textarea
                rows={5}
                data-testid={`legal-section-${i}-paragraphs`}
                value={(s.paragraphs || []).map((p) => p.it).join("\n\n")}
                onChange={(e) => {
                  const old = s.paragraphs || [];
                  const paragraphs = e.target.value.split("\n\n").map((t, k) => ({ it: t, ...(old[k]?.en ? { en: old[k].en } : {}) }));
                  setSection(i, { ...s, paragraphs });
                }}
                className={fieldCls}
              />
            </div>
            {s.list !== undefined && (
              <div>
                <label className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground mb-1.5">
                  Elenco puntato <span className="normal-case tracking-normal">(una voce per riga)</span>
                </label>
                <textarea
                  rows={4}
                  data-testid={`legal-section-${i}-list`}
                  value={(s.list || []).map((li) => li.it).join("\n")}
                  onChange={(e) => {
                    const old = s.list || [];
                    const list = e.target.value.split("\n").map((t, k) => ({ it: t, ...(old[k]?.en ? { en: old[k].en } : {}) }));
                    setSection(i, { ...s, list });
                  }}
                  className={fieldCls}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function UsersManager({ me }) {
  const [users, setUsers] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [creating, setCreating] = useState(false);
  const [pw, setPw] = useState({});
  const [savingPw, setSavingPw] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [myPw, setMyPw] = useState({ current: "", next: "" });
  const [savingMyPw, setSavingMyPw] = useState(false);
  const fieldCls =
    "w-full bg-onyx/60 border border-ice/10 rounded-lg px-3.5 py-2.5 text-sm text-ice outline-none focus:border-royal transition-colors";

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (err) {
      toast.error(formatApiError(err));
      setUsers([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const createUser = async () => {
    if (!form.email || !form.password) {
      toast.error("Email e password sono obbligatorie");
      return;
    }
    setCreating(true);
    try {
      await api.post("/users", form);
      toast.success(`Utente ${form.email} creato`);
      setForm({ name: "", email: "", password: "" });
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const changePassword = async (id) => {
    const password = pw[id] || "";
    if (password.length < 8) {
      toast.error("La password deve avere almeno 8 caratteri");
      return;
    }
    setSavingPw(id);
    try {
      await api.put(`/users/${id}`, { password });
      toast.success("Password aggiornata");
      setPw({ ...pw, [id]: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingPw(null);
    }
  };

  const deleteUser = async (id, email) => {
    if (!window.confirm(`Eliminare l'utente ${email}?`)) return;
    setDeleting(id);
    try {
      await api.delete(`/users/${id}`);
      toast.success("Utente eliminato");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleting(null);
    }
  };

  const changeMyPassword = async () => {
    if (!myPw.current || myPw.next.length < 8) {
      toast.error("Inserisci la password attuale e una nuova password di almeno 8 caratteri");
      return;
    }
    setSavingMyPw(true);
    try {
      await api.put("/auth/password", { current_password: myPw.current, new_password: myPw.next });
      toast.success("La tua password è stata aggiornata");
      setMyPw({ current: "", next: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingMyPw(false);
    }
  };

  if (users === null) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="animate-spin text-azure" size={24} />
      </div>
    );
  }

  return (
    <div data-testid="users-manager" className="space-y-10">
      <div>
        <h1 className="font-display text-2xl font-bold mb-2">Utenti amministratori</h1>
        <p className="text-xs text-muted-foreground">Chi può accedere al CMS. Ogni utente ha pieni poteri di modifica.</p>
      </div>

      <div className="bg-surface border border-ice/8 rounded-xl p-5">
        <p className="font-mono text-xs uppercase tracking-[0.15em] text-royal mb-4">La mia password ({me?.email})</p>
        <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3">
          <input
            data-testid="my-password-current"
            type="password"
            placeholder="Password attuale"
            value={myPw.current}
            onChange={(e) => setMyPw({ ...myPw, current: e.target.value })}
            className={fieldCls}
            autoComplete="current-password"
          />
          <input
            data-testid="my-password-new"
            type="password"
            placeholder="Nuova password (min. 8 caratteri)"
            value={myPw.next}
            onChange={(e) => setMyPw({ ...myPw, next: e.target.value })}
            className={fieldCls}
            autoComplete="new-password"
          />
          <button
            data-testid="my-password-save"
            onClick={changeMyPassword}
            disabled={savingMyPw}
            className="inline-flex items-center justify-center gap-2 bg-royal hover:bg-azure hover:text-[#0B1424] transition-colors text-white text-sm font-semibold px-5 py-2.5 rounded-lg disabled:opacity-60"
          >
            {savingMyPw ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />}
            Aggiorna
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {users.map((u) => (
          <div key={u.id} data-testid={`user-item-${u.id}`} className="bg-surface border border-ice/8 rounded-xl p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-display font-semibold text-ice">
                  {u.name} {u.id === me?.id && <span className="text-xs font-mono text-azure">(tu)</span>}
                </p>
                <p className="text-sm text-muted-foreground">{u.email}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-royal bg-royal/10 rounded-full px-3 py-1">{u.role}</span>
                {u.id !== me?.id && (
                  <button
                    data-testid={`user-delete-${u.id}`}
                    onClick={() => deleteUser(u.id, u.email)}
                    disabled={deleting === u.id}
                    className="text-muted-foreground hover:text-red-400 transition-colors disabled:opacity-50"
                  >
                    {deleting === u.id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                  </button>
                )}
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <input
                data-testid={`user-password-input-${u.id}`}
                type="password"
                placeholder="Nuova password per questo utente"
                value={pw[u.id] || ""}
                onChange={(e) => setPw({ ...pw, [u.id]: e.target.value })}
                className={`${fieldCls} max-w-xs`}
                autoComplete="new-password"
              />
              <button
                data-testid={`user-password-save-${u.id}`}
                onClick={() => changePassword(u.id)}
                disabled={savingPw === u.id || !(pw[u.id] || "")}
                className="inline-flex items-center gap-2 border border-royal/40 text-royal hover:bg-royal hover:text-white transition-colors text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-50"
              >
                {savingPw === u.id ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Imposta password
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-surface border border-ice/8 rounded-xl p-5">
        <p className="font-mono text-xs uppercase tracking-[0.15em] text-royal mb-4">Aggiungi amministratore</p>
        <div className="grid sm:grid-cols-3 gap-3">
          <input
            data-testid="user-create-name"
            placeholder="Nome"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={fieldCls}
          />
          <input
            data-testid="user-create-email"
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className={fieldCls}
            autoComplete="off"
          />
          <input
            data-testid="user-create-password"
            type="password"
            placeholder="Password (min. 8 caratteri)"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className={fieldCls}
            autoComplete="new-password"
          />
        </div>
        <button
          data-testid="user-create-submit"
          onClick={createUser}
          disabled={creating}
          className="mt-4 inline-flex items-center gap-2 bg-royal hover:bg-azure hover:text-[#0B1424] transition-colors text-white text-sm font-semibold px-5 py-2.5 rounded-full disabled:opacity-60"
        >
          {creating ? <Loader2 size={15} className="animate-spin" /> : <Users size={15} />}
          Crea utente
        </button>
      </div>
    </div>
  );
}

function LeadsTable({ leads, onDeleted }) {
  const [deleting, setDeleting] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const remove = async (id) => {
    setDeleting(id);
    try {
      await api.delete(`/contacts/${id}`);
      onDeleted(id);
      toast.success("Richiesta eliminata");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(null);
    }
  };
  if (leads === null) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="animate-spin text-azure" size={24} />
      </div>
    );
  }
  return (
    <div data-testid="admin-leads-table">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <h1 className="font-display text-2xl font-bold">Richieste di contatto ({leads.length})</h1>
        <a
          data-testid="leads-export-csv"
          href={`${API}/contacts/export`}
          className="inline-flex items-center gap-2 border border-royal/40 text-royal hover:bg-royal hover:text-white transition-colors text-sm font-semibold px-4 py-2 rounded-full"
        >
          <Download size={15} />
          Esporta CSV
        </a>
      </div>
      {leads.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nessuna richiesta ricevuta finora.</p>
      ) : (
        <div className="space-y-4">
          {leads.map((l) => (
            <div key={l.id} className="bg-surface border border-ice/8 rounded-xl overflow-hidden">
              <button
                data-testid={`lead-expand-${l.id}`}
                onClick={() => setExpanded(expanded === l.id ? null : l.id)}
                className="w-full text-left p-5 hover:bg-onyx/40 transition-colors"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="font-display font-semibold text-ice">
                      {l.nome} <span className="text-muted-foreground font-normal">· {l.azienda}</span>
                    </p>
                    {l.email_status && (
                      <span
                        data-testid={`lead-email-status-${l.id}`}
                        title={l.email_status === "error" ? (l.email_error || "Errore invio email") : ""}
                        className={`font-mono text-[10px] uppercase tracking-[0.12em] rounded-full px-2.5 py-1 ${
                          l.email_status === "sent"
                            ? "bg-emerald-500/15 text-emerald-400"
                            : l.email_status === "error"
                              ? "bg-red-500/15 text-red-400"
                              : "bg-ice/10 text-muted-foreground"
                        }`}
                      >
                        {l.email_status === "sent"
                          ? "Notifica: Inviata"
                          : l.email_status === "error"
                            ? "Notifica: Errore"
                            : "Notifica: —"}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-xs text-muted-foreground">
                      {l.created_at ? new Date(l.created_at).toLocaleString("it-IT") : ""}
                    </span>
                    <span
                      data-testid={`lead-delete-${l.id}`}
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(l.id);
                      }}
                      onKeyDown={(e) => e.key === "Enter" && remove(l.id)}
                      className="text-muted-foreground hover:text-red-400 transition-colors"
                    >
                      {deleting === l.id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                    </span>
                    {expanded === l.id ? (
                      <ChevronDown size={16} className="text-azure rotate-180 transition-transform" />
                    ) : (
                      <ChevronDown size={16} className="text-muted-foreground transition-transform" />
                    )}
                  </div>
                </div>
                <p className="mt-1 text-sm text-azure">{l.email}</p>
              </button>

              {expanded === l.id && (
                <div data-testid={`lead-detail-${l.id}`} className="border-t border-ice/8 px-5 py-5 bg-onyx/30">
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 text-sm">
                    {[
                      ["Email", l.email],
                      ["Telefono", l.telefono],
                      ["Ruolo", l.ruolo],
                      ["Consumo annuo", l.consumo_gwh],
                      ["Tipologia cliente", l.tipologia_cliente],
                      ["Scadenza contratto", l.scadenza_contratto],
                      ["Lingua", (l.lang || "it").toUpperCase()],
                      ["Ricevuta il", l.created_at ? new Date(l.created_at).toLocaleString("it-IT") : ""],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{label}</p>
                        <p className="mt-0.5 text-ice">{value || "—"}</p>
                      </div>
                    ))}
                  </div>
                  {l.messaggio && (
                    <div className="mt-5">
                      <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Messaggio</p>
                      <p className="mt-1.5 text-sm text-ice/85 leading-relaxed whitespace-pre-wrap">{l.messaggio}</p>
                    </div>
                  )}
                  {l.allegato_nome && (
                    <a
                      data-testid={`lead-attachment-${l.id}`}
                      href={`${API}/contact/${l.id}/attachment`}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-5 inline-flex items-center gap-2 border border-royal/40 text-royal hover:bg-royal hover:text-white transition-colors text-sm font-semibold px-4 py-2 rounded-full"
                    >
                      <Download size={15} />
                      Scarica bolletta — {l.allegato_nome}
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
